import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';

import { useNavigationStack } from '../../../context/NavigationStackContext';
import { useTimer as useAppTimer } from '../../../context/TimerContext';
import { useTrainingExercises } from '../../../hooks/useTrainingExercises';
import { useGetExerciseOptions } from '../../../redux/api/exerciseConfigAPI';
import {
  trainingsApi,
  useBulkUpdateTrainingSets,
  useDeleteTraining,
  useGetTrainingById,
  usePatchTrainingSetsOrder,
} from '../../../redux/api/trainingsAPI';
import { useGetMyDetails } from '../../../redux/api/users/usersApi';
import { TRAININGS } from '../../../redux/constants';
import { TimerType, TrainingStatus } from '../../../shared/constants';
import { Paths } from '../../../shared/paths';
import { ITraining } from '../../../types/interfaces/training';
import { ITrainingSet } from '../../../types/interfaces/training-set';
import { groupBy } from '../../../utils/collections/array-utils';
import { orderTraining } from '../../../utils/training/set-utils';
import { useEditTraining } from '../edit-training/useEditTraining';
import { ITimerConfig } from './interfaces';

export function useCurrentTrainingPage() {
  const { trainingId } = useParams<{ trainingId: string }>();
  const {
    data: training,
    isLoading: isCurrentTrainingLoadingState,
    isFetching: isCurrentTrainingFetchingState,
    isUninitialized: isCurrentTrainingUninitialized,
    isError: isCurrentTrainingError,
    error: currentTrainingError,
    refetch: refetchCurrentTraining,
    isPreloadedCached,
  } = useGetTrainingById(trainingId, { skip: !trainingId });
  const [deleteTrainingApi, { isLoading: isDeleting }] = useDeleteTraining();
  const {
    exercises,
    isLoading: isTrainingExercisesLoading,
    isError: isTrainingExercisesError,
  } = useTrainingExercises(training?.id);
  const [patchTrainingSetsOrder, { isLoading: isSavingSetsOrder }] =
    usePatchTrainingSetsOrder();
  const [bulkUpdateTrainingSets, { isLoading: isUpdatingTrainingSets }] =
    useBulkUpdateTrainingSets();
  const [activeTimer, setActiveTimer] = useState<ITimerConfig | null>(null);
  const { data: user } = useGetMyDetails();
  const { navigate } = useNavigationStack();
  const [editedTrainingSetGroup, setEditedTrainingSetGroup] =
    useState<ITrainingSet[]>(null);
  const [searchParams] = useSearchParams();
  const editParam = searchParams.get('edit');
  const [trainingEditing, setTrainingEditing] = useState(editParam === 'true');
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [sortSetsModalOpen, setSortSetsModalOpen] = useState(false);

  useEffect(() => {
    if (training?.permissions && !training.permissions.canEditInfo) {
      setTrainingEditing(false);
    }
  }, [training]);
  const { getTimer, isTimerRunning, timers } = useAppTimer();
  const [startTimerValue, setStartTimerValue] = useState<number | null>(null);
  const [showStartTimerButton, setShowStartTimerButton] = useState(false);
  const [timerButtomActive, setTimerButtomActive] = useState(false);
  const dispatch = useDispatch();

  const timerRunning = useMemo(() => {
    return isTimerRunning(training?.id);
  }, [timers, training?.id]);

  useEffect(() => {
    return () => {
      if (trainingId) {
        dispatch(
          trainingsApi.util.invalidateTags([
            { type: TRAININGS.TRAININGS, id: trainingId },
          ]),
        );
      }
    };
  }, [dispatch, trainingId]);

  useEffect(() => {
    setTimerButtomActive(!!training?.id && !!timerRunning);
  }, [training?.id, timerRunning]);

  useEffect(() => {
    const status =
      currentTrainingError && 'status' in currentTrainingError
        ? (currentTrainingError as { status: number }).status
        : undefined;
    if (isCurrentTrainingError && status === 404) {
      navigate(Paths.trainings.toList, { replace: true });
    }
  }, [isCurrentTrainingError, currentTrainingError, navigate]);

  useEffect(() => {
    if (!trainingId) {
      navigate(Paths.trainings.toList, { replace: true });
    }
  }, [trainingId, navigate]);

  useEffect(() => {
    if (
      !isCurrentTrainingUninitialized &&
      !isCurrentTrainingLoadingState &&
      !isCurrentTrainingFetchingState &&
      !isCurrentTrainingError &&
      !training &&
      !isPreloadedCached
    ) {
      navigate(Paths.trainings.toList, { replace: true });
    }
  }, [
    isCurrentTrainingUninitialized,
    isCurrentTrainingLoadingState,
    isCurrentTrainingFetchingState,
    isCurrentTrainingError,
    training,
    isPreloadedCached,
    navigate,
  ]);

  useEffect(() => {
    if (!training || !user) {
      return;
    }

    if (training.permissions?.canOpenLikeCurrent === false) {
      if (training.status === TrainingStatus.finished) {
        navigate(Paths.trainings.toFinished(training.id), { replace: true });
      } else {
        navigate(Paths.trainings.toViewId(training.id), { replace: true });
      }
      return;
    }

    if (training.status === TrainingStatus.notStarted) {
      navigate(Paths.trainings.toViewId(training.id), { replace: true });
    } else if (training.status === TrainingStatus.finished) {
      navigate(Paths.trainings.toFinished(training.id), { replace: true });
    } else if (training.permissions?.canOpenLikeCurrent !== true) {
      navigate(Paths.trainings.toViewId(training.id), { replace: true });
    }
  }, [training, user, navigate]);

  const { getEmptyTrainingSet } = useEditTraining(null, training?.id);
  const {
    data: exerciseOptions,
    isLoading: isExerciseOptionsLoadingState,
    isFetching: isExerciseOptionsFetchingState,
    isError: isExerciseOptionsError,
  } = useGetExerciseOptions();

  const orderedTraining: ITraining = useMemo(() => {
    if (!training) return null;
    if (!exercises?.length || !exerciseOptions) return null;

    return orderTraining(training, exercises, exerciseOptions);
  }, [training, exercises, exerciseOptions]);

  const trainingSetGroups = useMemo(
    () =>
      orderedTraining?.trainingSets
        ? groupBy(orderedTraining.trainingSets, 'setNumber')
        : {},
    [orderedTraining],
  );

  const isCurrentTrainingLoading = useMemo(() => {
    if (!training) {
      return isCurrentTrainingLoadingState;
    }

    const isExerciseOptionsLoading =
      !exerciseOptions &&
      (isExerciseOptionsLoadingState || isExerciseOptionsFetchingState);
    const isDependenciesLoading =
      isTrainingExercisesLoading || isExerciseOptionsLoading;
    const hasDependenciesError =
      isTrainingExercisesError || isExerciseOptionsError;

    return isDependenciesLoading && !hasDependenciesError;
  }, [
    training,
    isCurrentTrainingLoadingState,
    isTrainingExercisesLoading,
    isExerciseOptionsLoadingState,
    isExerciseOptionsFetchingState,
    isTrainingExercisesError,
    isExerciseOptionsError,
  ]);

  const activateTimer = useCallback((value: number) => {
    setStartTimerValue(value);
    setTimeout(() => {
      setShowStartTimerButton(true);
    }, 100);
    setTimeout(() => {
      hideStartTimerButton();
    }, 5000);
  }, []);

  function deactivateTimer() {
    setActiveTimer(null);
  }

  function onEditTrainingSetSubmit(trainingSets: ITrainingSet[]) {
    const updatedTrainingSets = trainingSets.map((set) => ({
      ...set,
      trainingSetItems: set.trainingSetItems.map((item) => ({
        ...item,
        trainingSetItemOptions: item.trainingSetItemOptions.map((option) => ({
          ...option,
          status: undefined,
          value: undefined,
          value2: undefined,
        })),
      })),
    }));

    bulkUpdateTrainingSets({
      trainingId: training.id,
      sets: updatedTrainingSets,
    })
      .unwrap()
      .then((r) => {
        setEditedTrainingSetGroup(null);
      })
      .catch((err) => console.log(err));
  }

  function onEditTrainingSetClick(trainingSetGroup: ITrainingSet[]) {
    setEditedTrainingSetGroup(trainingSetGroup);
  }

  function onNewTrainingSetClick() {
    setEditedTrainingSetGroup([
      getEmptyTrainingSet(training.trainingSets, training.id),
    ]);
  }

  async function onSaveSetsOrder(
    sets: { id: string; setNumber: number; orderNumber: number }[],
  ) {
    if (!training?.id) return;
    await patchTrainingSetsOrder({
      trainingId: training.id,
      sets,
    }).unwrap();
    setSortSetsModalOpen(false);
  }

  function onRunButtonClick() {
    setActiveTimer({
      time: startTimerValue,
      startValue: startTimerValue,
      type: TimerType.time,
      timerId: training.id,
      autostart: false,
      onFinish: () => {},
      label: '',
    });
    hideStartTimerButton();
  }

  function hideStartTimerButton() {
    setShowStartTimerButton(false);
    setStartTimerValue(null);
  }

  function onTimerButtonClick() {
    const timer = getTimer(training.id);

    setActiveTimer({
      time: timer?.value || 0,
      startValue: timer?.startTime || 0,
      type: timer?.type || TimerType.time,
      timerId: training.id,
      autostart: !!timer?.timestamp,
      onFinish: () => {},
      label: '',
    });
  }

  function deleteTraining() {
    if (!training?.id) return;
    deleteTrainingApi({ id: training.id })
      .unwrap()
      .then(() => {
        setShowDeleteConfirmation(false);
        navigate(Paths.trainings.toList, { replace: true });
      })
      .catch(console.error);
  }

  return {
    training,
    orderedTrainingSets: orderedTraining?.trainingSets ?? [],
    isCurrentTrainingLoading,
    isCurrentTrainingError,
    currentTrainingError,
    refetchCurrentTraining,
    trainingSetGroups,
    activateTimer,
    activeTimer,
    isSavingTrainingSets: isSavingSetsOrder || isUpdatingTrainingSets,
    isSavingSetsOrder,
    editedTrainingSetGroup,
    setEditedTrainingSetGroup,
    onEditTrainingSetSubmit,
    onEditTrainingSetClick,
    onNewTrainingSetClick,
    trainingEditing,
    setTrainingEditing,
    sortSetsModalOpen,
    setSortSetsModalOpen,
    onSaveSetsOrder,
    deactivateTimer,
    startTimerValue,
    onRunButtonClick,
    onTimerButtonClick,
    showStartTimerButton,
    timerButtomActive,
    showDeleteConfirmation,
    setShowDeleteConfirmation,
    deleteTraining,
    isDeleting,
  };
}
